import { forwardRef } from 'react';
import { Popover, Dropdown } from 'rsuite';
import { icons } from '@shared/static/icons';
import {
  useEditCardDialog,
  useChangeCardOwnerDialog,
  useFreezeCardDialog,
  useArchiveCardDialog,
  useBlockCardDialog,
  useDeleteCardDialog,
  useUnfreezeCardDialog,
  useChangeLimitsCardDialog,
  useTransferDialog,
} from '@utils/dialogs-hooks';
import { useTranslation } from 'react-i18next';
import { cardStatuses } from '@utils/statuses';
import { useDispatch, useSelector } from 'react-redux';
import { roles } from '@shared/static/roles';
import {
  setTransactionsFiltersNoFetch,
  fetchTransactions,
} from '@app/store/slices/transactionsSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDialogContext } from '@app/components/dialogs/DialogProvider';

export const CardMenu = forwardRef(
  ({ close, data, hideTopUpBin, hideEdit, ...rest }, ref) => {
    const { t } = useTranslation();
    const { openEditCardDialog } = useEditCardDialog();
    const { openChangeCardOwnerDialog } = useChangeCardOwnerDialog();
    const { openFreezeCardDialog } = useFreezeCardDialog();
    const { openArchiveCardDialog } = useArchiveCardDialog();
    const { openBlockCardDialog } = useBlockCardDialog();
    const { openDeleteCardDialog } = useDeleteCardDialog();
    const { openUnfreezeCardDialog } = useUnfreezeCardDialog();
    const { openTransferDialog } = useTransferDialog();
    const { params } = useSelector((state) => state.cards);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { closeModal } = useDialogContext();
    function handleSelectMenu() {
      close();
    }

    const isArchiveTab = params?.filter === 'archive';

    const isFrozen = cardStatuses[data?.status] === 'frozen';
    const isBlocked = cardStatuses[data?.status] === 'blocked';

    const { user } = useSelector((state) => state.user);

    function openTransactionsPageWithFilters() {
      dispatch(
        setTransactionsFiltersNoFetch({
          params: { page: 1, bank_id: data?.details?.bank_id },
          search: data?.details?.cardNumber?.slice(-4) || null,
        }),
      );
      if (location.pathname === '/transactions') {
        dispatch(fetchTransactions());
      } else {
        navigate('/transactions');
      }
      closeModal();
    }

    return (
      <Popover ref={ref} {...rest} full data-skip-row-click>
        <Dropdown.Menu onSelect={handleSelectMenu} className="dropdown-menu">
          {isFrozen && (
            <Dropdown.Item
              onSelect={() => openUnfreezeCardDialog([data])}
              icon={icons.unfreeze}
            >
              {t('unfreeze')}
            </Dropdown.Item>
          )}
          {!hideTopUpBin && !isFrozen && !isBlocked && (
            <Dropdown.Item
              onSelect={() => {
                openTransferDialog({
                  bank_id: data?.details?.bank_id,
                  bank_from: 0,
                });
              }}
              icon={icons.transfer}
            >
              {t('top_up_bin')}
            </Dropdown.Item>
          )}
          {!hideEdit && (
            <Dropdown.Item
              onSelect={() => openEditCardDialog(data)}
              icon={icons.edit}
            >
              {t('edit')}
            </Dropdown.Item>
          )}
          {!isBlocked && user.group_role !== roles.TEAM_MEMBER && (
            <Dropdown.Item
              onSelect={() => openChangeCardOwnerDialog(data)}
              icon={icons['users-group']}
            >
              {t('change_owner')}
            </Dropdown.Item>
          )}
          <Dropdown.Item
            onSelect={openTransactionsPageWithFilters}
            icon={icons.transactions}
          >
            {t('view_transactions')}
          </Dropdown.Item>
          <Dropdown.Separator />
          {!isFrozen && !isBlocked && (
            <Dropdown.Item
              onSelect={() => openFreezeCardDialog([data])}
              icon={icons.freeze}
              className="freeze-color"
            >
              {t('freeze')}
            </Dropdown.Item>
          )}
          <Dropdown.Item
            onSelect={() => openArchiveCardDialog([data])}
            icon={icons.archive}
          >
            {isArchiveTab ? t('unarchive') : t('archive')}
          </Dropdown.Item>
          {!isBlocked && (
            <Dropdown.Item
              onSelect={() => openBlockCardDialog([data])}
              icon={icons['block-outline']}
            >
              {t('block')}
            </Dropdown.Item>
          )}
          {user.group_role !== roles.TEAM_MEMBER && (
            <Dropdown.Item
              onSelect={() => openDeleteCardDialog([data])}
              icon={icons.delete}
              className="delete-color"
            >
              {t('delete')}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Popover>
    );
  },
);

export const SelectedCardsMenu = forwardRef(({ close, data, ...rest }, ref) => {
  const { t } = useTranslation();
  const { openUnfreezeCardDialog } = useUnfreezeCardDialog();
  const { openFreezeCardDialog } = useFreezeCardDialog();
  const { openArchiveCardDialog } = useArchiveCardDialog();
  const { openBlockCardDialog } = useBlockCardDialog();
  const { openDeleteCardDialog } = useDeleteCardDialog();
  const { openChangeLimitsCardDialog } = useChangeLimitsCardDialog();
  const { params } = useSelector((state) => state.cards);
  const isArchiveTab = params?.filter === 'archive';
  const { user } = useSelector((state) => state.user);

  function handleSelectMenu() {
    close();
  }

  const allBlocked = data.every(
    (item) => cardStatuses[item?.status] === 'blocked',
  );
  const isFreezeAvailable = data.some((item) => {
    const status = cardStatuses[item?.status];
    return status !== 'frozen' && status !== 'blocked';
  });

  const hasFrozen = data.some(
    (item) => cardStatuses[item?.status] === 'frozen',
  );

  return (
    <Popover data-skip-row-click ref={ref} {...rest} full>
      <Dropdown.Menu onSelect={handleSelectMenu} className="dropdown-menu">
        {!allBlocked && (
          <Dropdown.Item
            icon={icons.edit}
            onSelect={() => openChangeLimitsCardDialog(data)}
          >
            {t('change_limits_menu_item')}
          </Dropdown.Item>
        )}
        {hasFrozen && (
          <>
            <Dropdown.Item
              onSelect={() => openUnfreezeCardDialog(data)}
              icon={icons.unfreeze}
            >
              {t('unfreeze')}
            </Dropdown.Item>
            <Dropdown.Separator />
          </>
        )}
        {isFreezeAvailable && (
          <>
            <Dropdown.Item
              onSelect={() => openFreezeCardDialog(data)}
              icon={icons.freeze}
              className="freeze-color"
            >
              {t('freeze')}
            </Dropdown.Item>
            <Dropdown.Separator />
          </>
        )}
        <Dropdown.Item
          onSelect={() => openArchiveCardDialog(data)}
          icon={icons.archive}
        >
          {isArchiveTab ? t('unarchive') : t('archive')}
        </Dropdown.Item>
        {!allBlocked && (
          <Dropdown.Item
            onSelect={() => openBlockCardDialog(data)}
            icon={icons['block-outline']}
          >
            {t('block')}
          </Dropdown.Item>
        )}
        {user.group_role !== roles.TEAM_MEMBER && (
          <Dropdown.Item
            className="delete-color"
            onSelect={() => openDeleteCardDialog(data)}
            icon={icons.delete}
          >
            {t('delete')}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Popover>
  );
});
